import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, FileText, Download } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useReports, useBuildings, useAllRooms } from '@/hooks'
import {
  PageHeader,
  EmptyState,
  DataTable,
  SortableHeader,
} from '@/components'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toCSV, downloadCSV } from '@/lib/csv'
import type { Report } from '@/types'

export function ReportsPage() {
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()

  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('')
  const [selectedRoomId, setSelectedRoomId] = useState<string>('')

  const { data: buildings } = useBuildings(effectiveOrgId)
  const { data: allRooms } = useAllRooms()

  // Filter rooms by selected building
  const filteredRooms = selectedBuildingId
    ? allRooms?.filter((r) => r.building_id === selectedBuildingId)
    : allRooms

  const { data: reports, isLoading, error } = useReports({
    buildingId: selectedBuildingId || undefined,
    roomId: selectedRoomId || undefined,
  })

  // Super admin with no org selected
  if (user?.role === 'super_admin' && !effectiveOrgId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Reports"
          description="View and manage cleaning reports"
        />
        <EmptyState
          icon={<FileText className="h-12 w-12" />}
          title="No Organization Selected"
          description="Select an organization using the switcher in the header to view reports."
        />
      </div>
    )
  }

  // Create maps for display
  const buildingMap = new Map(buildings?.map((b) => [b.id, b.name]) ?? [])
  const roomMap = new Map(allRooms?.map((r) => [r.id, r.name]) ?? [])

  // Reset room filter when building changes
  const handleBuildingChange = (value: string) => {
    setSelectedBuildingId(value === 'all' ? '' : value)
    setSelectedRoomId('')
  }

  const handleRoomChange = (value: string) => {
    setSelectedRoomId(value === 'all' ? '' : value)
  }

  const handleExportCSV = () => {
    if (!reports || reports.length === 0) return

    const csv = toCSV(reports, [
      {
        key: 'created_at',
        header: 'Date',
      },
      {
        key: (row) => {
          const date = new Date(row.created_at)
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        },
        header: 'Time',
      },
      {
        key: (row) => buildingMap.get(row.building_id) ?? row.building_id,
        header: 'Building',
      },
      {
        key: (row) => roomMap.get(row.room_id) ?? row.room_id,
        header: 'Room',
      },
      {
        key: (row) => row.checklist.filter((item) => item.checked).length,
        header: 'Items Completed',
      },
      {
        key: (row) => row.checklist.length,
        header: 'Total Items',
      },
      {
        key: (row) => {
          const checked = row.checklist.filter((item) => item.checked).length
          const total = row.checklist.length
          return total > 0 ? Math.round((checked / total) * 100) : 0
        },
        header: 'Completion %',
      },
      {
        key: 'version',
        header: 'Version',
      },
      {
        key: (row) => row.notes ?? '',
        header: 'Notes',
      },
    ])

    // Generate filename with filter context
    const parts = ['reports']
    if (selectedBuildingId) {
      const buildingName = buildingMap.get(selectedBuildingId)
      if (buildingName) parts.push(buildingName.replace(/\s+/g, '-'))
    }
    if (selectedRoomId) {
      const roomName = roomMap.get(selectedRoomId)
      if (roomName) parts.push(roomName.replace(/\s+/g, '-'))
    }
    const date = new Date().toISOString().split('T')[0]
    parts.push(date)

    downloadCSV(csv, `${parts.join('_')}.csv`)
  }

  const columns: ColumnDef<Report>[] = [
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <SortableHeader column={column}>Date</SortableHeader>
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'))
        return (
          <Link
            to={`/reports/${row.original.id}`}
            className="font-medium text-primary hover:underline"
          >
            {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Link>
        )
      },
    },
    {
      accessorKey: 'building_id',
      header: ({ column }) => (
        <SortableHeader column={column}>Building</SortableHeader>
      ),
      cell: ({ row }) => {
        const buildingId = row.getValue('building_id') as string
        return (
          <Link
            to={`/buildings/${buildingId}`}
            className="text-primary hover:underline"
          >
            {row.original.building_name ?? buildingMap.get(buildingId) ?? 'Unknown'}
          </Link>
        )
      },
    },
    {
      accessorKey: 'room_id',
      header: ({ column }) => (
        <SortableHeader column={column}>Room</SortableHeader>
      ),
      cell: ({ row }) => {
        const roomId = row.getValue('room_id') as string
        return (
          <Link
            to={`/rooms/${roomId}`}
            className="text-primary hover:underline"
          >
            {row.original.room_name ?? roomMap.get(roomId) ?? 'Unknown'}
          </Link>
        )
      },
    },
    {
      id: 'completion',
      header: 'Status',
      cell: ({ row }) => {
        const checklist = row.original.checklist
        const checked = checklist.filter((item) => item.checked).length
        const total = checklist.length
        const percent = total > 0 ? Math.round((checked / total) * 100) : 0

        return (
          <div className="flex items-center gap-2">
            <Badge variant={percent === 100 ? 'default' : 'secondary'}>
              {checked}/{total}
            </Badge>
            <span className="text-sm text-muted-foreground">{percent}%</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'version',
      header: 'Version',
      cell: ({ row }) => (
        <span className="text-muted-foreground">v{row.getValue('version')}</span>
      ),
    },
  ]

  if (error) {
    return (
      <div className="text-destructive">
        Failed to load reports: {error.message}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        description="View and manage cleaning reports"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={!reports || reports.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            {(user?.role === 'super_admin' || user?.role === 'admin') && (
              <Button asChild>
                <Link to="/reports/new">
                  <Plus className="mr-2 h-4 w-4" />
                  New Report
                </Link>
              </Button>
            )}
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-6">
        <div className="flex w-[220px] flex-col gap-1.5">
          <label className="text-sm font-medium text-muted-foreground">Building</label>
          <Select value={selectedBuildingId || 'all'} onValueChange={handleBuildingChange}>
            <SelectTrigger>
              <SelectValue placeholder="All buildings" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All buildings</SelectItem>
              {buildings?.map((building) => (
                <SelectItem key={building.id} value={building.id}>
                  {building.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[220px] flex-col gap-1.5">
          <label className="text-sm font-medium text-muted-foreground">Room</label>
          <Select
            value={selectedRoomId || 'all'}
            onValueChange={handleRoomChange}
            disabled={!filteredRooms?.length}
          >
            <SelectTrigger>
              <SelectValue placeholder="All rooms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All rooms</SelectItem>
              {filteredRooms?.map((room) => (
                <SelectItem key={room.id} value={room.id}>
                  {room.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Loading reports...</div>
      ) : reports && reports.length > 0 ? (
        <DataTable columns={columns} data={reports} pageSize={20} />
      ) : (
        <EmptyState
          icon={<FileText className="h-12 w-12" />}
          title="No reports found"
          description={
            selectedBuildingId || selectedRoomId
              ? 'No reports match your filters. Try adjusting your selection.'
              : 'Create your first cleaning report to get started.'
          }
          action={
            (user?.role === 'super_admin' || user?.role === 'admin') ? (
              <Button asChild>
                <Link to="/reports/new">
                  <Plus className="mr-2 h-4 w-4" />
                  New Report
                </Link>
              </Button>
            ) : undefined
          }
        />
      )}
    </div>
  )
}
